import { useMemo, useState } from 'react';
import { CartesianGrid, ComposedChart, Line, Scatter, XAxis, YAxis } from 'recharts';
import { FlaskConical, History, RotateCw } from 'lucide-react';
import type { SimulationBatch } from '@/types/evaluator';
import { DataSurface } from '@/components/platform/DataSurface';
import { Button } from '@/components/ui/button';
import { TooltipLabel } from '@/components/ui/TooltipLabel';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { CHART_TOGGLE_BUTTON_CLASS, HISTORY_CHART_CONFIG, HISTORY_TIME_RANGE_LABELS, HISTORY_TIME_RANGES } from '../constants';
import type { HistoryChartMode, HistoryTimeRange } from '../types';
import { getAgentVersionDisplay, getSimulationConversationCount, getSimulationOutcome, getSimulationRunAverage, getRunHistoryChartData, getVersionChartData, formatDate, isWithinTimeRange } from '../utils';
import { HistoryChartTooltip, HistoryRunDot, VersionChartTooltip } from '../components/HistoryChartParts';
import { AgentVersionCell, RunStatusBadge } from '../components/status';

export function HistoryView({
  runs,
  selectedRunId,
  onSelectRun,
  onRetryRun,
  retryingRunId,
}: {
  runs: SimulationBatch[];
  selectedRunId: string | null;
  onSelectRun: (id: string) => void;
  onRetryRun: (id: string) => void;
  retryingRunId: string | null;
}) {
  const [chartMode, setChartMode] = useState<HistoryChartMode>('time');
  const [timeRange, setTimeRange] = useState<HistoryTimeRange>(90);
  const [retryConfirmRun, setRetryConfirmRun] = useState<SimulationBatch | null>(null);
  const allTimeChartData = useMemo(() => getRunHistoryChartData(runs), [runs]);
  const timeChartData = useMemo(
    () => allTimeChartData.filter((point) => isWithinTimeRange(point.rawDate, timeRange)),
    [allTimeChartData, timeRange],
  );
  const versionChartData = useMemo(() => getVersionChartData(runs), [runs]);
  const chartData = chartMode === 'time' ? timeChartData : versionChartData;
  const hasScoredRuns = chartData.some((point) => point.score != null);
  const emptyChartMessage = chartMode === 'time'
    ? `No scored runs in the past ${timeRange} days`
    : 'No versioned runs yet';

  if (runs.length === 0) {
    return (
      <DataSurface>
        <div className="border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">
              <TooltipLabel
                label="History"
                description="Previous simulation runs for this organization."
              />
            </p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <FlaskConical className="h-8 w-8 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium text-foreground">No simulations yet</p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Run your first simulation once a population and agent version are ready.
            </p>
          </div>
        </div>
      </DataSurface>
    );
  }

  return (
    <DataSurface>
      <div className="border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            <TooltipLabel
              label="History"
              description="Previous simulation runs for this organization."
            />
          </p>
        </div>
      </div>
      <div className="border-b px-4 py-3">
        <div className="mb-2 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-foreground">Simulation Performance</p>
            <div className="inline-flex rounded-md border border-border/70 bg-muted/70 p-[2px]">
              <Button
                type="button"
                data-state={chartMode === 'time' ? 'on' : 'off'}
                variant="ghost"
                size="sm"
                className={CHART_TOGGLE_BUTTON_CLASS}
                onClick={() => setChartMode('time')}
              >
                Over time
              </Button>
              <Button
                type="button"
                data-state={chartMode === 'version' ? 'on' : 'off'}
                variant="ghost"
                size="sm"
                className={CHART_TOGGLE_BUTTON_CLASS}
                onClick={() => setChartMode('version')}
              >
                By version
              </Button>
            </div>
            {!hasScoredRuns ? (
              <span className="text-xs text-muted-foreground">{emptyChartMessage}</span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {chartMode === 'time' ? (
              <div className="inline-flex rounded-md border border-border/70 bg-muted/70 p-[2px]">
                {HISTORY_TIME_RANGES.map((range) => (
                  <Button
                    key={range}
                    type="button"
                    data-state={timeRange === range ? 'on' : 'off'}
                    variant="ghost"
                    size="sm"
                    className={CHART_TOGGLE_BUTTON_CLASS}
                    onClick={() => setTimeRange(range)}
                  >
                    {HISTORY_TIME_RANGE_LABELS[range]}
                  </Button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <ChartContainer config={HISTORY_CHART_CONFIG} className="aspect-auto h-[180px] w-full">
          <ComposedChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid
              vertical={false}
              strokeDasharray="3 3"
              className="stroke-[hsl(var(--border))]"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              width={34}
              domain={[0, 100]}
              tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
            />
            <XAxis
              dataKey={chartMode === 'time' ? 'ts' : 'version'}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={42}
              tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value) => {
                if (chartMode === 'version') return String(value);
                try {
                  return new Date(value).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  });
                } catch {
                  return '--';
                }
              }}
            />
            <ChartTooltip
              cursor={{
                strokeWidth: 1,
                strokeDasharray: '4 4',
                className: 'stroke-[hsl(var(--border))]',
              }}
              content={chartMode === 'time' ? <HistoryChartTooltip /> : <VersionChartTooltip />}
            />
            <Line
              dataKey="score"
              type="monotone"
              stroke="hsl(var(--primary))"
              strokeWidth={1.5}
              dot={false}
              connectNulls={false}
            />
            <Scatter
              dataKey="plotScore"
              shape={(props: any) => <HistoryRunDot {...props} onSelectRun={onSelectRun} />}
            />
          </ComposedChart>
        </ChartContainer>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-[minmax(140px,1fr)_130px_110px_130px_100px_160px] gap-3 border-b bg-muted/20 px-4 py-2 text-[11px] font-medium uppercase text-muted-foreground">
            <span>Date</span>
            <span>Agent Version</span>
            <span>Population Count</span>
            <span>Conversation Count</span>
            <span>Average Score</span>
            <span className="text-right">Status</span>
          </div>
          <div className="divide-y">
            {runs.map((run) => {
              const isSelected = run.id === selectedRunId;
              const populations = run.dataset_names?.length ?? 0;
              const conversations = getSimulationConversationCount(run);
              const score = getSimulationRunAverage(run);
              const agentVersion = getAgentVersionDisplay(run);
              const outcome = getSimulationOutcome(run.status);
              const canRetry = outcome === 'failed';
              return (
                <div
                  key={run.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectRun(run.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelectRun(run.id);
                    }
                  }}
                  className={cn(
                    'grid w-full cursor-pointer grid-cols-[minmax(140px,1fr)_130px_110px_130px_100px_160px] gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40',
                    isSelected && 'bg-muted/50',
                  )}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{formatDate(run.created_at)}</p>
                  </div>
                  <AgentVersionCell display={agentVersion} />
                  <div className="text-sm text-muted-foreground">
                    {populations}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {conversations}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {score ?? '—'}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    {canRetry ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-6 gap-1 px-1.5 text-[11px] font-normal"
                        disabled={retryingRunId === run.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          setRetryConfirmRun(run);
                        }}
                      >
                        <RotateCw className={cn('h-3 w-3', retryingRunId === run.id && 'animate-spin')} />
                        Retry
                      </Button>
                    ) : null}
                    <RunStatusBadge status={run.status} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <AlertDialog
        open={retryConfirmRun !== null}
        onOpenChange={(open) => {
          if (!open) setRetryConfirmRun(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retry this simulation?</AlertDialogTitle>
            <AlertDialogDescription>
              {retryConfirmRun
                ? `This will re-run the failed simulation from ${formatDate(retryConfirmRun.created_at)} against the same agent version and populations.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (retryConfirmRun) {
                  onRetryRun(retryConfirmRun.id);
                  setRetryConfirmRun(null);
                }
              }}
            >
              Retry simulation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DataSurface>
  );
}
